import React, { useCallback, useEffect, useState } from 'react'
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore'
import { db } from '../../firebase-config'
import { DEFAULT_ITEM, emptyWorkoutItem, estimateMinutes, formatClock, isWorkoutDoc } from '../../utils/workout'

const emptyForm = {
  title: '',
  description: '',
  difficulty: 'beginner',
  category: '',
  items: []
}

const inputClass =
  'shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'

function WorkoutBuilder({ categories, user }) {
  const [exercises, setExercises] = useState([])
  const [workouts, setWorkouts] = useState([])
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState(null)
  const [selectedExerciseId, setSelectedExerciseId] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)

  const fetchExercises = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'exercises'))
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
      setExercises(list.sort((a, b) => (a.title || '').localeCompare(b.title || '')))
    } catch (error) {
      console.error('Error fetching exercises:', error)
    }
  }, [])

  const fetchWorkouts = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'workouts'))
      const list = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(isWorkoutDoc)
      setWorkouts(list)
    } catch (error) {
      console.error('Error fetching workouts:', error)
    }
  }, [])

  useEffect(() => {
    fetchExercises()
    fetchWorkouts()
  }, [fetchExercises, fetchWorkouts])

  const handleFieldChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleAddExercise = () => {
    const exercise = exercises.find(item => item.id === selectedExerciseId)
    if (!exercise) return
    setForm(prev => ({ ...prev, items: [...prev.items, emptyWorkoutItem(exercise)] }))
    setSelectedExerciseId('')
  }

  const updateItem = (index, changes) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, ...changes } : item))
    }))
  }

  const removeItem = (index) => {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }))
  }

  const moveItem = (index, direction) => {
    const target = index + direction
    setForm(prev => {
      if (target < 0 || target >= prev.items.length) return prev
      const items = [...prev.items]
      const [moved] = items.splice(index, 1)
      items.splice(target, 0, moved)
      return { ...prev, items }
    })
  }

  const resetForm = () => {
    setForm(emptyForm)
    setEditingId(null)
    setSelectedExerciseId('')
  }

  const handleEdit = (workout) => {
    setEditingId(workout.id)
    setForm({
      title: workout.title || '',
      description: workout.description || '',
      difficulty: workout.difficulty || 'beginner',
      category: workout.category || '',
      // Backfill anything a doc saved by an older version might be missing.
      items: (workout.items || []).map(item => ({ ...DEFAULT_ITEM, ...item }))
    })
    setMessage(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (form.items.length === 0) {
      setMessage({ type: 'error', text: 'Add at least one exercise to the workout.' })
      return
    }

    setSaving(true)
    setMessage(null)

    // Numbers come out of the inputs as strings; store them as numbers so the
    // player and the duration estimate don't have to keep parsing.
    const payload = {
      title: form.title,
      description: form.description,
      difficulty: form.difficulty,
      category: form.category,
      items: form.items.map(item => ({
        exerciseId: item.exerciseId,
        exerciseTitle: item.exerciseTitle,
        mode: item.mode === 'timed' ? 'timed' : 'reps',
        sets: Math.max(1, parseInt(item.sets, 10) || 1),
        reps: Math.max(0, parseInt(item.reps, 10) || 0),
        workSeconds: Math.max(0, parseInt(item.workSeconds, 10) || 0),
        restSeconds: Math.max(0, parseInt(item.restSeconds, 10) || 0),
        notes: item.notes || ''
      }))
    }

    try {
      if (editingId) {
        await updateDoc(doc(db, 'workouts', editingId), { ...payload, updatedAt: new Date() })
        setMessage({ type: 'success', text: 'Workout updated.' })
      } else {
        await addDoc(collection(db, 'workouts'), {
          ...payload,
          createdAt: new Date(),
          createdBy: user?.email || null
        })
        setMessage({ type: 'success', text: 'Workout created.' })
      }
      resetForm()
      fetchWorkouts()
    } catch (error) {
      console.error('Error saving workout:', error)
      setMessage({ type: 'error', text: 'Could not save the workout. Please try again.' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this workout?')) return
    try {
      await deleteDoc(doc(db, 'workouts', id))
      if (editingId === id) resetForm()
      fetchWorkouts()
    } catch (error) {
      console.error('Error deleting workout:', error)
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Builder */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {editingId ? 'Edit Workout' : 'Build a Workout'}
          </h2>
          {editingId && (
            <button onClick={resetForm} className="text-sm text-gray-500 hover:text-gray-700 underline">
              Cancel edit
            </button>
          )}
        </div>

        {exercises.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm p-3 rounded mb-4">
            There are no exercises in the library yet. Add exercises on the Exercises tab first.
          </div>
        )}

        {message && (
          <div className={`text-sm p-3 rounded mb-4 ${
            message.type === 'error'
              ? 'bg-red-50 border border-red-300 text-red-700'
              : 'bg-green-50 border border-green-300 text-green-700'
          }`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="workoutTitle">
              Title
            </label>
            <input
              type="text"
              id="workoutTitle"
              name="title"
              value={form.title}
              onChange={handleFieldChange}
              className={inputClass}
              placeholder="e.g. Full Body Blast"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="workoutDescription">
              Description
            </label>
            <textarea
              id="workoutDescription"
              name="description"
              value={form.description}
              onChange={handleFieldChange}
              className={inputClass}
              rows="3"
            ></textarea>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="workoutDifficulty">
                Difficulty
              </label>
              <select
                id="workoutDifficulty"
                name="difficulty"
                value={form.difficulty}
                onChange={handleFieldChange}
                className={inputClass}
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="workoutCategory">
                Category
              </label>
              <select
                id="workoutCategory"
                name="category"
                value={form.category}
                onChange={handleFieldChange}
                className={inputClass}
              >
                <option value="">No category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Exercise sequence */}
          <div className="border-t pt-4 mb-4">
            <div className="flex justify-between items-baseline mb-3">
              <h3 className="text-lg font-semibold text-gray-700">Exercises in order</h3>
              {form.items.length > 0 && (
                <span className="text-sm text-gray-500">
                  about {estimateMinutes(form)} min
                </span>
              )}
            </div>

            <div className="flex gap-2 mb-4">
              <select
                value={selectedExerciseId}
                onChange={(e) => setSelectedExerciseId(e.target.value)}
                className={`${inputClass} flex-1`}
              >
                <option value="">Add an exercise…</option>
                {exercises.map((exercise) => (
                  <option key={exercise.id} value={exercise.id}>
                    {exercise.title}{exercise.category ? ` (${exercise.category})` : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddExercise}
                disabled={!selectedExerciseId}
                className="bg-gray-700 hover:bg-gray-800 disabled:opacity-40 text-white px-4 py-2 rounded text-sm whitespace-nowrap"
              >
                Add
              </button>
            </div>

            {form.items.length === 0 ? (
              <p className="text-gray-500 text-sm">No exercises added yet.</p>
            ) : (
              <ol className="space-y-3">
                {form.items.map((item, index) => (
                  <li key={`${item.exerciseId}-${index}`} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <span className="font-bold text-sm">
                        {index + 1}. {item.exerciseTitle}
                      </span>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => moveItem(index, -1)}
                          disabled={index === 0}
                          className="px-2 py-1 text-xs bg-white border rounded disabled:opacity-30"
                          aria-label="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveItem(index, 1)}
                          disabled={index === form.items.length - 1}
                          className="px-2 py-1 text-xs bg-white border rounded disabled:opacity-30"
                          aria-label="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="px-2 py-1 text-xs text-red-600 hover:text-red-800 border border-red-200 rounded"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <label className="text-xs text-gray-600">
                        Type
                        <select
                          value={item.mode}
                          onChange={(e) => updateItem(index, { mode: e.target.value })}
                          className="mt-1 block w-full border rounded px-2 py-1 text-sm text-gray-700"
                        >
                          <option value="reps">Reps</option>
                          <option value="timed">Timed</option>
                        </select>
                      </label>

                      <label className="text-xs text-gray-600">
                        Sets
                        <input
                          type="number"
                          min="1"
                          value={item.sets}
                          onChange={(e) => updateItem(index, { sets: e.target.value })}
                          className="mt-1 block w-full border rounded px-2 py-1 text-sm text-gray-700"
                        />
                      </label>

                      {item.mode === 'timed' ? (
                        <label className="text-xs text-gray-600">
                          Work (sec)
                          <input
                            type="number"
                            min="1"
                            value={item.workSeconds}
                            onChange={(e) => updateItem(index, { workSeconds: e.target.value })}
                            className="mt-1 block w-full border rounded px-2 py-1 text-sm text-gray-700"
                          />
                        </label>
                      ) : (
                        <label className="text-xs text-gray-600">
                          Reps
                          <input
                            type="number"
                            min="1"
                            value={item.reps}
                            onChange={(e) => updateItem(index, { reps: e.target.value })}
                            className="mt-1 block w-full border rounded px-2 py-1 text-sm text-gray-700"
                          />
                        </label>
                      )}

                      <label className="text-xs text-gray-600">
                        Rest (sec)
                        <input
                          type="number"
                          min="0"
                          value={item.restSeconds}
                          onChange={(e) => updateItem(index, { restSeconds: e.target.value })}
                          className="mt-1 block w-full border rounded px-2 py-1 text-sm text-gray-700"
                        />
                      </label>
                    </div>

                    <input
                      type="text"
                      value={item.notes}
                      onChange={(e) => updateItem(index, { notes: e.target.value })}
                      placeholder="Coaching note shown during the set (optional)"
                      className="mt-2 block w-full border rounded px-2 py-1 text-sm text-gray-700"
                    />
                  </li>
                ))}
              </ol>
            )}
          </div>

          <button
            type="submit"
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
          >
            {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Workout'}
          </button>
        </form>
      </div>

      {/* Existing workouts */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Existing Workouts</h2>
        {workouts.length === 0 ? (
          <p className="text-gray-500">No workouts built yet.</p>
        ) : (
          <div className="space-y-4">
            {workouts.map((workout) => (
              <div key={workout.id} className="border-b pb-4">
                <div className="flex justify-between items-start">
                  <h3 className="font-bold">{workout.title}</h3>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleEdit(workout)}
                      className="text-indigo-600 hover:text-indigo-800 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(workout.id)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-1">{workout.description}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    {workout.difficulty}
                  </span>
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                    ~{estimateMinutes(workout)} min
                  </span>
                  <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">
                    {workout.items.length} exercises
                  </span>
                  {workout.category && (
                    <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                      {workout.category}
                    </span>
                  )}
                </div>
                <ol className="mt-3 text-xs text-gray-500 space-y-1">
                  {workout.items.map((item, index) => (
                    <li key={`${item.exerciseId}-${index}`}>
                      {index + 1}. {item.exerciseTitle} — {item.sets} ×{' '}
                      {item.mode === 'timed' ? formatClock(item.workSeconds) : `${item.reps} reps`}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default WorkoutBuilder
